import * as React from 'react'

import {
  Box,
  GammaTable,
  Icon,
  PaginationButton,
  SortDirection,
  Text,
  theme,
} from '@factorialco/deprecated-design-system'
import keys from 'lodash/keys'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import { useFitTablePaginationHeight } from 'modules/core/hooks/useFitTablePaginationHeight'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/reports/components/BlankSlate'
import { cleanHeaderNames, formatForSQL, formatter } from 'modules/reports/lib/Constants'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { pathWithFunction } from 'modules/reports/lib/projectionHelper'
import fields from 'modules/reports/store/collections/fields'

const MAX_DISPLAYED_PAGES = 5

interface Props {
  data: Array<Record<string, unknown>>
  reportStruct: ReportStruct
  withinDashboardItem?: boolean
}

/**
 * Type of a single row in the data array.
 */
type DataRow = Props['data'][number]

type PaginationOptionsProps = {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const PaginationOptions = ({ currentPage, totalPages, onPageChange }: PaginationOptionsProps) => {
  const prevButtonDisabled = currentPage === 1
  const nextButtonDisabled = currentPage === totalPages
  const displayedPages = Math.min(totalPages, MAX_DISPLAYED_PAGES)
  const ellipsize = totalPages > MAX_DISPLAYED_PAGES

  let pages = Array.from({ length: displayedPages }, (_, i) => i + 1)

  if (ellipsize && currentPage > 3) {
    // put the current page in the center, remember pages are 1-indexed
    pages = Array.from({ length: displayedPages }, (_, i) => i + currentPage - 2)
  }

  if (ellipsize && currentPage > totalPages - 3) {
    // unless it's the last 3 pages, remember pages are 1-indexed
    pages = Array.from({ length: displayedPages }, (_, i) => i + totalPages - 4)
  }

  return (
    <Box
      flexDirection="row"
      gap="s6"
      justifyContent="center"
      alignItems="center"
      component="nav"
      aria-label={i18n.t('common.pagination.wrapper')}
    >
      <PaginationButton
        content={
          <Icon.Medium icon="chevron-left" color={prevButtonDisabled ? 'grey400' : 'grey600'} />
        }
        ariaLabel={i18n.t('common.pagination.previous')}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={prevButtonDisabled}
        nonInteractive
      />
      {pages[0] !== 1 && <Text.Normal>...</Text.Normal>}
      <Box flexDirection="row" gap="s4" alignItems="center">
        {pages.map((index) => (
          <Box aria-current={index === currentPage ? 'true' : 'false'} key={index} component="span">
            <PaginationButton
              content={index}
              ariaLabel={i18n.t('common.pagination.go_to_page', { page: index })}
              onClick={() => onPageChange(index)}
              highlighted={index === currentPage}
            />
          </Box>
        ))}
      </Box>
      {pages[pages.length - 1] !== totalPages && <Text.Normal>...</Text.Normal>}
      <PaginationButton
        content={
          <Icon.Medium icon="chevron-right" color={nextButtonDisabled ? 'grey400' : 'grey600'} />
        }
        ariaLabel={i18n.t('common.pagination.next')}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={nextButtonDisabled}
        nonInteractive
      />
    </Box>
  )
}

/**
 * Renders a widget's information in a table format
 *
 * @constructor
 * @param data - provide raw data from reports controller
 * @param reportStruct - provide reportStruct in order to read the query projections
 */
const Table = ({ data, reportStruct, withinDashboardItem }: Props) => {
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc')
  const [sortAttribute, setSortAttribute] = React.useState<string>('')

  const handleSort = React.useCallback((attribute: string, direction: SortDirection) => {
    setSortDirection(direction)
    setSortAttribute(attribute)
  }, [])

  const sortedData = React.useMemo(
    () => orderBy(data, [sortAttribute], [sortDirection]),
    [data, sortAttribute, sortDirection]
  )

  const {
    totalPages,
    currentPage,
    setCurrentPage,
    currentPageRows,
    containerRef,
    tableRef,
    bodyRef,
    rowRef,
  } = useFitTablePaginationHeight(sortedData)

  function getValueLabel(row: DataRow, path: string) {
    const projections = reportStruct.props.projections

    // When Report is a SqlQuery we do not have projections
    if (!projections.length) return formatForSQL(row[path], path)

    const projection =
      projections.find((p) => p.path === (path === 'cohort' ? 'time_period.cohort' : path)) ||
      projections.find((p) => pathWithFunction(p) === path) ||
      reportStruct.props.projections[0]

    const value = row[path]

    return projection && formatter(projection)(value)
  }

  if (totalPages === 0) {
    return (
      <BlankSlate
        message={i18n.t('custom_reports.editor.no_results')}
        illustration="task-list-search"
      />
    )
  }

  const headerNames = keys(data[0])

  return (
    <Box ref={containerRef} height="full">
      <Box ref={tableRef} height="fit-content">
        <GammaTable
          autoLayout
          autoWidth
          hideBorder={withinDashboardItem}
          style={
            withinDashboardItem
              ? {
                  borderBottom: `1px solid ${theme.colors.grey400}`,
                }
              : undefined
          }
          optionsBottom={() => (
            <PaginationOptions
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        >
          <GammaTable.Head>
            <GammaTable.Row>
              {headerNames.map((column, index: number) => {
                const fieldPath = cleanHeaderNames(column)

                const field = fields.find({
                  path: fieldPath,
                })

                return (
                  <GammaTable.SortableHeadCell
                    onSort={handleSort}
                    sortAttribute={sortAttribute}
                    sortDirection={sortDirection}
                    attribute={column}
                    key={`head-cell-${column}-${index}`}
                  >
                    {field ? field.attributes.label : fieldPath}
                  </GammaTable.SortableHeadCell>
                )
              })}
            </GammaTable.Row>
          </GammaTable.Head>
          <GammaTable.Body ref={bodyRef}>
            {currentPageRows.map((row, index) => (
              <GammaTable.Row key={`row-${index}`} trRef={index === 0 ? rowRef : undefined}>
                {headerNames.map((column) => (
                  <GammaTable.Cell key={`cell-${column}`}>
                    <Text.Normal ellipsis>{getValueLabel(row, column)}</Text.Normal>
                  </GammaTable.Cell>
                ))}
              </GammaTable.Row>
            ))}
          </GammaTable.Body>
        </GammaTable>
      </Box>
    </Box>
  )
}

export default observer(Table)
